// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { formatNumber } from "@sequentech/ui-core";
import { VotesTimeResolution } from "./votesTimeRange";
var MAX_AXIS_LABELS = 8;
var MAX_TOTAL_LABELS = 24;
function parseLocalBucket(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(value);
    if (!match) {
        return null;
    }
    var year = match[1], month = match[2], day = match[3], _a = match[4], hour = _a === void 0 ? "0" : _a, _b = match[5], minute = _b === void 0 ? "0" : _b, _c = match[6], second = _c === void 0 ? "0" : _c;
    var date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    if (date.getFullYear() !== Number(year) ||
        date.getMonth() !== Number(month) - 1 ||
        date.getDate() !== Number(day) ||
        date.getHours() !== Number(hour) ||
        date.getMinutes() !== Number(minute)) {
        return null;
    }
    return date;
}
export function formatVotesBucket(bucket, resolution, locale, detailed) {
    if (detailed === void 0) { detailed = false; }
    var date = parseLocalBucket(bucket);
    if (!date) {
        return bucket;
    }
    if (resolution === VotesTimeResolution.DAY) {
        return new Intl.DateTimeFormat(locale, {
            weekday: detailed ? "short" : undefined,
            year: detailed ? "numeric" : undefined,
            month: "short",
            day: "numeric",
        }).format(date);
    }
    return new Intl.DateTimeFormat(locale, {
        weekday: detailed ? "short" : undefined,
        year: detailed ? "numeric" : undefined,
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}
export var getVotesPerDayChartOptions = function (_a) {
    var buckets = _a.buckets, resolution = _a.resolution, locale = _a.locale, numberFormatPolicy = _a.numberFormatPolicy;
    var labelInterval = Math.max(1, Math.ceil(buckets.length / MAX_AXIS_LABELS));
    var formatCount = function (count) { return formatNumber(count, numberFormatPolicy); };
    return {
        chart: {
            id: "barchart-votes",
            stacked: true,
        },
        dataLabels: {
            enabled: false,
        },
        legend: {
            showForZeroSeries: false,
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    total: {
                        enabled: buckets.length <= MAX_TOTAL_LABELS,
                        formatter: formatCount,
                    },
                },
            },
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            x: {
                formatter: function (value, options) {
                    var _a;
                    var bucket = (_a = buckets[options === null || options === void 0 ? void 0 : options.dataPointIndex]) !== null && _a !== void 0 ? _a : String(value);
                    return formatVotesBucket(bucket, resolution, locale, true);
                },
            },
            y: {
                formatter: formatCount,
            },
        },
        xaxis: {
            categories: buckets,
            labels: {
                hideOverlappingLabels: true,
                rotate: 0,
                formatter: function (value) {
                    var index = buckets.indexOf(value);
                    var shouldShow = index === buckets.length - 1 || (index >= 0 && index % labelInterval === 0);
                    return shouldShow ? formatVotesBucket(value, resolution, locale) : "";
                },
            },
        },
        yaxis: {
            labels: {
                // The steps of a small range can be fractional; they keep one decimal.
                formatter: function (value) {
                    return formatNumber(value, numberFormatPolicy, Number.isInteger(value) ? 0 : 1);
                },
            },
        },
    };
};
